import { useState, useEffect } from "react";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
} from "reactstrap";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const OrdersTable = () => {
  const [filter, setFilter] = useState("");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [mappingModalOpen, setMappingModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [assignTo, setAssignTo] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [status, setStatus] = useState("Open");
  const [notes, setNotes] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  //const [assignedUser, setAssignedUser] = useState("");
  const [threatsData, setThreatsData] = useState([]);
  const [selectedThreats, setSelectedThreats] = useState([]);
  const [mappingFilter, setMappingFilter] = useState("");
  
  const [tableRows, setTableRows] = useState([
    {
      id: "AST-1",
      control: {
        title: "Asset disposal procedures utilized",
        desc: "The company has electronic media containing confidential information purged or destroyed",
      },
      owner: "Unassigned",
      source: "Internal",
      frameworks: ["NCA ECC", "SACS-002", "ISO 27001", "SOC 2"],
      frameworkControls: {
        "NCA ECC": "ECC-08.1",
        "SACS-002": "SACS-DM-03",
        "ISO 27001": "A.8.3.2",
        "SOC 2": "CC 6.5"
      },
      tests: "2/2",
      testStatus: "success",
      mappedThreats: [], // Will store threat IDs
    },
    {
      id: "AST-2",
      control: {
        title: "Data retention procedures established",
        desc: "The company has formal retention and disposal procedures in place",
      },
      owner: "Unassigned",
      source: "Internal",
      frameworks: ["NCA ECC", "SACS-002", "ISO 27001"],
      frameworkControls: {
        "NCA ECC": "ECC-08.2",
        "SACS-002": "SACS-DM-04",
        "ISO 27001": "A.8.3.1"
      },
      tests: "2/2",
      testStatus: "success",
      mappedThreats: [],
    },
    {
      id: "BCD-1",
      control: {
        title: "Continuity and Disaster Recovery plans established",
        desc: "The company has Business Continuity and Disaster Recovery Plans in place",
      },
      owner: "Unassigned",
      source: "Internal",
      frameworks: ["NCA ECC", "ISO 22301", "SACS-002"],
      frameworkControls: {
        "NCA ECC": "ECC-12.1",
        "ISO 22301": "8.4.1",
        "SACS-002": "SACS-BC-01"
      },
      tests: "2/2",
      testStatus: "success",
      mappedThreats: [],
    },
    {
      id: "CONF-1",
      control: {
        title: "Configuration Management Policy",
        desc: "A documented policy exists for managing configurations of information systems",
      },
      owner: "John Doe",
      source: "Internal Audit",
      frameworks: ["NCA ECC", "SACS-002", "ISO 27001", "NIST CSF"],
      frameworkControls: {
        "NCA ECC": "ECC-05.1",
        "SACS-002": "SACS-CM-01",
        "ISO 27001": "A.12.1.2",
        "NIST CSF": "PR.IP-1"
      },
      tests: "1/2",
      testStatus: "warning",
      mappedThreats: [],
    },
    {
      id: "AUTH-1",
      control: {
        title: "Multi-factor authentication enforced",
        desc: "MFA is required for all user accounts accessing critical systems",
      },
      owner: "Jane Smith",
      source: "Security Team",
      frameworks: ["NCA ECC", "PCI_DSS", "NIST_800_53"],
      frameworkControls: {
        "NCA ECC": "ECC-05.2",
        "PCI_DSS": "8.3",
        "NIST_800_53": "IA-2(1)"
      },
      tests: "3/3",
      testStatus: "success",
      mappedThreats: [],
    },
  ]);

  // Fetch threats data
  useEffect(() => {
    const fetchThreats = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/threats`);
        const data = await response.json();
        setThreatsData(data);
        
        // Auto-map threats to controls based on compliance data
        autoMapThreatsToControls(data);
      } catch (error) {
        console.error("Error fetching threats:", error);
      }
    };
    fetchThreats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Automatically map threats to controls based on matching frameworks and control IDs
  const autoMapThreatsToControls = (threats) => {
    const updatedRows = tableRows.map(control => {
      const matchingThreats = threats.filter(threat => {
        // Check compliance_mappings
        const hasMatchingCompliance = threat.compliance_mappings?.some(comp => {
          const matchesFramework = control.frameworks.includes(comp.framework);
          const matchesControl = control.frameworkControls[comp.framework] === comp.control_id;
          return matchesFramework && matchesControl;
        });

        // Check framework_violations
        const hasMatchingViolation = threat.framework_violations?.some(violation => {
          const matchesFramework = control.frameworks.includes(violation.framework);
          const matchesControl = control.frameworkControls[violation.framework] === violation.control_id;
          return matchesFramework && matchesControl;
        });

        return hasMatchingCompliance || hasMatchingViolation;
      });

      return {
        ...control,
        mappedThreats: matchingThreats.map(t => t.threat_id)
      };
    });

    setTableRows(updatedRows);
  };

  const toggleDropdown = (idx) => {
    setOpenDropdown(openDropdown === idx ? null : idx);
  };

  const openAssignModal = (item) => {
    setSelectedItem(item);
    setModalOpen(true);
    setOpenDropdown(null);
    setAssignTo("");
    setDueDate("");
    setStatus("Open");
    setNotes("");
  };

  const openMappingModal = (item) => {
    setSelectedItem(item);
    setSelectedThreats(item.mappedThreats || []);
    setMappingModalOpen(true);
    setOpenDropdown(null);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedItem(null);
  };

  const closeMappingModal = () => {
    setMappingModalOpen(false);
    setSelectedItem(null);
    setSelectedThreats([]);
    setMappingFilter("");
  };

  const handleAssign = () => {
    const updatedRows = tableRows.map(row => 
      row.id === selectedItem.id 
        ? { ...row, owner: assignTo }
        : row
    );
    setTableRows(updatedRows);
    
    //setAssignedUser(assignTo);
    setShowSuccessMessage(true);
    
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
    
    closeModal();
  };

  const handleThreatToggle = (threatId) => {
    setSelectedThreats(prev => 
      prev.includes(threatId)
        ? prev.filter(id => id !== threatId)
        : [...prev, threatId]
    );
  };

  const handleSaveMapping = () => {
    const updatedRows = tableRows.map(row => 
      row.id === selectedItem.id 
        ? { ...row, mappedThreats: selectedThreats }
        : row
    );
    setTableRows(updatedRows);
    
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
    
    closeMappingModal();
  };

  const users = [
    "John Doe",
    "Jane Smith", 
    "Mike Johnson",
    "Sarah Wilson",
    "David Brown"
  ];

  const getBadgeClass = (status) => {
    switch (status) {
      case "success": return "badge bg-success";
      case "warning": return "badge bg-warning text-dark";
      case "danger": return "badge bg-danger";
      default: return "badge bg-secondary";
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "Critical": return "danger";
      case "High": return "warning";
      case "Medium": return "primary";
      case "Low": return "success";
      default: return "secondary";
    }
  };

  const getMappedThreatsDetails = (threatIds) => {
    return threatsData.filter(t => threatIds.includes(t.threat_id));
  };

  const filteredRows = tableRows.filter(
    (row) =>
      row.id.toLowerCase().includes(filter.toLowerCase()) ||
      row.control.title.toLowerCase().includes(filter.toLowerCase()) ||
      row.owner.toLowerCase().includes(filter.toLowerCase())
  );

  const filteredThreatsForMapping = threatsData.filter(threat =>
    threat.threat_type.toLowerCase().includes(mappingFilter.toLowerCase()) ||
    threat.threat_id.toLowerCase().includes(mappingFilter.toLowerCase()) ||
    threat.asset_name.toLowerCase().includes(mappingFilter.toLowerCase())
  );

  return (
    <div>
      {showSuccessMessage && (
        <div className="alert alert-success alert-dismissible fade show mb-3" role="alert">
          <i className="ri-check-circle-line me-2"></i>
          <strong>Success!</strong> Changes saved successfully.
          <button 
            type="button" 
            className="btn-close" 
            onClick={() => setShowSuccessMessage(false)}
          ></button>
        </div>
      )}
      
      <div className="card shadow-sm">
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center flex-grow-1">
            <i className="ri-search-line me-2 text-muted"></i>
            <input
              type="text"
              placeholder="Filter by ID, Control or Owner..."
              className="form-control border-0 shadow-none"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <div className="d-flex gap-2">
            <Badge color="primary" pill>
              {tableRows.reduce((sum, r) => sum + (r.mappedThreats?.length || 0), 0)} Mapped Threats
            </Badge>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-dark">
              <tr>
                <th>ID</th>
                <th>Control</th>
                <th>Owner</th>
                <th>Source</th>
                <th>Frameworks</th>
                <th>Mapped Threats</th>
                <th>Tests</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((item, idx) => {
                const mappedThreats = getMappedThreatsDetails(item.mappedThreats || []);
                
                return (
                  <tr key={idx}>
                    <td><strong>{item.id}</strong></td>
                    <td>
                      <div>
                        <div className="fw-semibold">{item.control.title}</div>
                        <div className="text-muted small">{item.control.desc}</div>
                      </div>
                    </td>
                    <td>{item.owner}</td>
                    <td>{item.source}</td>
                    <td>
                      {item.frameworks.map((fw, i) => (
                        <span key={i} className="badge bg-light text-dark me-1 mb-1 small">
                          {fw}
                        </span>
                      ))}
                    </td>
                    <td>
                      {mappedThreats.length > 0 ? (
                        <div className="d-flex flex-wrap gap-1">
                          <Badge color="primary" pill>
                            {mappedThreats.length} Threat{mappedThreats.length !== 1 ? 's' : ''}
                          </Badge>
                          {mappedThreats.slice(0, 2).map((threat, i) => (
                            <Badge 
                              key={i}
                              color={getSeverityColor(threat.asset_criticality)}
                              className="small"
                              title={threat.threat_type}
                            >
                              {threat.asset_criticality}
                            </Badge>
                          ))}
                          {mappedThreats.length > 2 && (
                            <Badge color="secondary" className="small">
                              +{mappedThreats.length - 2}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted small">Not mapped</span>
                      )}
                    </td>
                    <td>
                      <span className={getBadgeClass(item.testStatus)}>
                        {item.tests}
                      </span>
                    </td>
                    <td className="text-center">
                      <Dropdown
                        isOpen={openDropdown === idx}
                        toggle={() => toggleDropdown(idx)}
                      >
                        <DropdownToggle
                          caret={false}
                          className="btn btn-light btn-sm border-0"
                        >
                          <i className="ri-more-2-fill"></i>
                        </DropdownToggle>
                        <DropdownMenu end>
                          <DropdownItem onClick={() => openAssignModal(item)}>
                            <i className="ri-user-add-line me-2"></i> Assign
                          </DropdownItem>
                          <DropdownItem onClick={() => openMappingModal(item)}>
                            <i className="ri-links-line me-2"></i> Map Threats
                          </DropdownItem>
                        </DropdownMenu>
                      </Dropdown>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assignment Modal */}
      <Modal isOpen={modalOpen} toggle={closeModal} size="md">
        <ModalHeader toggle={closeModal} className="border-0 pb-2" style={{backgroundColor: '#e0f2fe'}}>
          <h5 className="mb-0" style={{color: '#0277bd'}}>Assign Control</h5>
        </ModalHeader>
        <ModalBody className="pt-3" style={{backgroundColor: '#fafafa'}}>
          {selectedItem && (
            <>
              <div className="mb-3 p-3 rounded shadow-sm" style={{
                backgroundColor: '#fff3e0',
                border: '1px solid #ffcc02'
              }}>
                <div className="fw-semibold" style={{color: '#e65100'}}>
                  {selectedItem.id}: {selectedItem.control.title}
                </div>
                <div className="text-muted small mt-1">{selectedItem.control.desc}</div>
              </div>

              <div className="p-3 rounded shadow-sm" style={{backgroundColor: 'white', border: '1px solid #e2e8f0'}}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Assign To <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={assignTo}
                        onChange={(e) => setAssignTo(e.target.value)}
                      >
                        <option value="">Select User</option>
                        {users.map((user, i) => (
                          <option key={i} value={user}>{user}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Due Date <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Status</label>
                  <select
                    className="form-select"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="Open">Open</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Under Review">Under Review</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Notes</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Enter any additional information..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter className="border-0 pt-0" style={{backgroundColor: '#fafafa'}}>
          <Button color="light" onClick={closeModal}>Cancel</Button>
          <Button 
            onClick={handleAssign}
            disabled={!assignTo || !dueDate}
            style={{ backgroundColor: '#0288d1', border: 'none', color: 'white' }}
          >
            Assign Control
          </Button>
        </ModalFooter>
      </Modal>

      {/* Threat Mapping Modal */}
      <Modal isOpen={mappingModalOpen} toggle={closeMappingModal} size="lg">
        <ModalHeader toggle={closeMappingModal} className="bg-primary text-white">
          <h5 className="mb-0">
            <i className="ri-links-line me-2"></i>
            Map Threats to Control
          </h5>
        </ModalHeader>
        <ModalBody>
          {selectedItem && (
            <>
              <div className="mb-3 p-3 rounded border bg-light">
                <div className="fw-semibold text-primary">{selectedItem.id}: {selectedItem.control.title}</div>
                <div className="text-muted small mt-1">{selectedItem.control.desc}</div>
                <div className="mt-2">
                  {selectedItem.frameworks.map((fw, i) => (
                    <span key={i} className="badge bg-primary me-1">
                      {fw}: {selectedItem.frameworkControls[fw]}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mb-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search threats by ID, type, or asset..."
                  value={mappingFilter}
                  onChange={(e) => setMappingFilter(e.target.value)}
                />
              </div>

              <div className="mb-2">
                <strong>{selectedThreats.length}</strong> threat(s) selected
              </div>

              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {filteredThreatsForMapping.length > 0 ? (
                  filteredThreatsForMapping.map((threat) => (
                    <div key={threat.threat_id} className="form-check p-3 border rounded mb-2 bg-white">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={selectedThreats.includes(threat.threat_id)}
                        onChange={() => handleThreatToggle(threat.threat_id)}
                        id={`threat-${threat.threat_id}`}
                      />
                      <label className="form-check-label w-100" htmlFor={`threat-${threat.threat_id}`}>
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <div className="fw-semibold">{threat.threat_type}</div>
                            <div className="small text-muted">
                              ID: {threat.threat_id} | Asset: {threat.asset_name}
                            </div>
                            <div className="small text-muted">
                              {new Date(threat.timestamp).toLocaleString()}
                            </div>
                            {threat.compliance_mappings && threat.compliance_mappings.length > 0 && (
                              <div className="mt-1">
                                {threat.compliance_mappings.map((comp, i) => (
                                  <span key={i} className="badge bg-info me-1 small">
                                    {comp.framework}: {comp.control_id}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <Badge color={getSeverityColor(threat.asset_criticality)}>
                            {threat.asset_criticality}
                          </Badge>
                        </div>
                      </label>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-muted py-4">
                    <i className="ri-inbox-line fs-1 mb-2 d-block opacity-25"></i>
                    <p>No threats found</p>
                  </div>
                )}
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={closeMappingModal}>Cancel</Button>
          <Button color="primary" onClick={handleSaveMapping}>
            <i className="ri-save-line me-2"></i>
            Save Mapping
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default OrdersTable;